import { useMemo } from 'react';
import { useRouter } from 'next/router';
import { useInboxRhPage_GetFeatureFlagsAndSettingsQuery } from '../types/GraphqlTypes';
import type { InboxRhPage_GetFeatureFlagsAndSettingsQuery } from '../types/GraphqlTypes';
import stringUtils from '../utils/stringUtils';
import apolloClient from '../lib/apollo';

type GetFeatureFlagsAndSettings = {
  features: NonNullable<
    NonNullable<InboxRhPage_GetFeatureFlagsAndSettingsQuery['getFeatureFlagsAndSettings']>['features']
  >;
  settings: NonNullable<
    NonNullable<InboxRhPage_GetFeatureFlagsAndSettingsQuery['getFeatureFlagsAndSettings']>['settings']
  >;
};

/**
 * @returns Available feature flags and whether or not they are enabled
 * @description Handles logic to determine if the feature flag is enabled by a param in the URL or set in the app config
 *      A feature flag can be enabled in the browser by setting the flag name as a param in the url with the value of `true`
 *      or set for all users in the app config
 */
export default (): GetFeatureFlagsAndSettings => {
  const { data: appConfigData } = useInboxRhPage_GetFeatureFlagsAndSettingsQuery({
    fetchPolicy: 'cache-first',
    client: apolloClient,
  });

  const router = useRouter();
  const config = appConfigData?.getFeatureFlagsAndSettings;
  const featureFlags = useMemo(() => config?.features ?? {}, [config]);
  const settings = useMemo(() => config?.settings ?? {}, [config]);

  const memoizedFeatureFlags = useMemo(() => {
    return Object.entries(featureFlags).reduce((accum, [flag, value]) => {
      const shouldShowFeatureFlagQueryParamValue = router.query[flag]?.toString() || '';

      return {
        ...accum,
        [flag]: value || stringUtils.isTrue(shouldShowFeatureFlagQueryParamValue),
      };
    }, {});
  }, [featureFlags, router.query]);

  return {
    features: memoizedFeatureFlags,
    settings,
  };
};
